import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiConsumer } from '../../service/api-consumer';
import { StatsDataRequest } from '../../interface/request/StatsDataRequest';
import { StatsFetchType } from '../../enum/StatsFetchType';
import { searchStats, StatsResponse } from '../../interface/stats/StatsResponse';
import { AgmsResponseCode } from '../../enum/AgmsResponseCode';
import { StatsGroupEnum } from '../../enum/StatsGroupEnum';
import { Currency } from '../../enum/Currency';

export const fetchStats = createAsyncThunk(
  'data/fetchStats',
  async ({ request, type }: { request: StatsDataRequest; type: StatsFetchType }) => {
    // Only send the actual request data to the API
    const response = await ApiConsumer.getStats(request);
    // Return both API data and type for reducer logic
    return { type, data: response };
  },
);

interface DataState {
  statsData: StatsResponse;
  filteredData: StatsResponse;
  shouldViewPerCurrency: boolean;
  shouldExpandSum: boolean;
  viewPerCurrencyData: StatsResponse;
  availableCurrencies: string[];
  groupBy: StatsGroupEnum;
  loading: boolean;
  error: unknown | null;
  locationColumnHeight: number;
  geoColumnHeight: number;
  shouldViewCurrencies: boolean;
  disableFooterSum: boolean;
  pendingRequests: number;
}

const initialState: DataState = {
  statsData: { Items: [], ResponseCode: AgmsResponseCode.OK, Message: '' },
  filteredData: { Items: [], ResponseCode: AgmsResponseCode.OK, Message: '' },
  viewPerCurrencyData: { Items: [], ResponseCode: AgmsResponseCode.OK, Message: '' },
  availableCurrencies: ['allCurrencies'],
  groupBy: StatsGroupEnum.Location,
  shouldViewPerCurrency: false,
  shouldExpandSum: false,
  loading: false,
  error: null,
  locationColumnHeight: 0,
  geoColumnHeight: 0,
  shouldViewCurrencies: false,
  disableFooterSum: false,
  pendingRequests: 0,
};

const statsSlice = createSlice({
  name: 'statsData',
  initialState,
  reducers: {
    applySearchInput(state, action) {
      state.filteredData = searchStats(state.statsData.Items || [], action.payload);
    },
    setShouldViewPerCurrency: (state) => {
      state.shouldViewPerCurrency = !state.shouldViewPerCurrency;
    },
    setShouldViewPerCurrencyFalse: (state) => {
      state.shouldViewPerCurrency = false;
    },
    setShouldExpandSum: (state, { payload }) => {
      state.shouldExpandSum = payload;
    },
    setDisableFooterSum: (state, { payload }) => {
      state.disableFooterSum = payload;
    },
    clearCurrencyData: (state) => {
      state.viewPerCurrencyData = {
        Items: [],
        ResponseCode: AgmsResponseCode.OK,
        Message: '',
      };
    },
    setSelectedGroupBy: (state, { payload }) => {
      state.groupBy = payload;
    },
    resetTableFooter: (state) => {
      state.shouldViewPerCurrency = false;
      state.shouldExpandSum = false;
      state.viewPerCurrencyData = {
        Items: [],
        ResponseCode: AgmsResponseCode.OK,
        Message: '',
      };
      state.groupBy = StatsGroupEnum.Location;
    },
    setViewPerCcyDialogHeight: (state, { payload }) => {
      const dialogHeight = Math.ceil((payload.numberOfRecords * payload.rowDensityHeight) / 10) * 10;
      const paddingRowHeight = Math.ceil(dialogHeight / payload.numberOfRecords);

      state.locationColumnHeight = dialogHeight - paddingRowHeight;
      state.geoColumnHeight = dialogHeight;
    },
    setShouldViewCurrencies: (state, { payload }) => {
      state.shouldViewCurrencies = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStats.pending, (state) => {
        state.pendingRequests += 1;

        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.pendingRequests = Math.max(0, state.pendingRequests - 1);
        state.loading = state.pendingRequests > 0;

        const { type, data } = action.payload;

        if (data.ResponseCode === AgmsResponseCode.OK) {
          if (type === StatsFetchType.VIEW_PER_CURRENCY || type === StatsFetchType.EXPAND_SUM_ITEM) {
            state.viewPerCurrencyData = data;
          } else if (type === StatsFetchType.EXPAND_SUM) {
            state.viewPerCurrencyData = data;
            state.availableCurrencies = ['allCurrencies'];
            state.viewPerCurrencyData.Items.forEach((item) => state.availableCurrencies.push(item.CCY));
            if (!state.availableCurrencies.includes(Currency.BAM)) {
              state.availableCurrencies.push(Currency.BAM);
            }

            if (!state.availableCurrencies.includes(Currency.EUR)) {
              state.availableCurrencies.push(Currency.EUR);
            }
            if (!state.availableCurrencies.includes(Currency.USD)) {
              state.availableCurrencies.push(Currency.USD);
            }
          } else {
            state.statsData = data;
            state.filteredData = data;
          }
        } else {
          state.error = data.Message;
          state.statsData = { ...data, Items: [] };
          state.filteredData = { ...data, Items: [] };
        }
      })
      .addCase(fetchStats.rejected, (state, action) => {
        state.pendingRequests = Math.max(0, state.pendingRequests - 1);
        state.loading = state.pendingRequests > 0;
        state.error = action.payload || action.error?.message;
      });
  },
});

export const {
  setShouldViewPerCurrency,
  setShouldViewPerCurrencyFalse,
  setShouldExpandSum,
  clearCurrencyData,
  setSelectedGroupBy,
  resetTableFooter,
  setViewPerCcyDialogHeight,
  applySearchInput,
  setShouldViewCurrencies,
  setDisableFooterSum,
} = statsSlice.actions;

export default statsSlice.reducer;
